import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import useToggleSidebar from "../../../components/togglesidebar";
import axios from "axios";
import API_URL from "../../../config/config";

import Sidebar from "../../sidebar";
import Header from "../../Header";
import Footer from "../../footer";

function AddUserDetails() {
    const navigate = useNavigate();
    const [alert, setAlert] = useState({ type: "", message: "" });

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        password: "",
        role: "admin",
        isActive: true,
    });

    const { isSidebarOpen, toggleSidebar } = useToggleSidebar();
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) {
            errors.name = "Name is required.";
        }
        if (!formData.email.trim()) {
            errors.email = "Email is required.";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = "Email address is invalid.";
        }
        if (!formData.password.trim()) {
            errors.password = "Password is required.";
        } else if (formData.password.length < 6) {
            errors.password = "Password must be at least 6 characters long.";
        }
        if (!formData.role) {
            errors.role = "Role is required.";
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
        if (validationErrors[e.target.name]) {
            setValidationErrors(prevErrors => ({
                ...prevErrors,
                [e.target.name]: ""
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const token = localStorage.getItem("adminToken");
            if (!token) {
                setAlert({ type: "danger", message: "Unauthorized: No token provided" });
                setLoading(false);
                return;
            }

            const response = await axios.post(`${API_URL}/users`, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data.success) {
                navigate("/admin/user", { state: { message: response.data.message || "Admin user added successfully!" } });
            } else {
                setAlert({ type: "danger", message: response.data.message || "Failed to add admin user" });
            }

        } catch (error) {
            if (error.response && error.response.data && error.response.data.errors) {
                const backendErrors = {};
                error.response.data.errors.forEach(err => {
                    backendErrors[err.path] = err.msg;
                });
                setValidationErrors(backendErrors);
                setAlert({ type: "danger", message: error.response.data.message || "Validation failed on server." });
            } else {
                setAlert({ type: "danger", message: error.response?.data?.message || "Server Error" });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Admin | Add Admin User</title>
            </Helmet>
            <Sidebar isSidebarOpen={isSidebarOpen} />
            <div
                className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`}
                onClick={() => isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()}
            >
                <div className="content-wrapper" style={{ marginLeft: isSidebarOpen ? "250px" : "70px", transition: "margin-left .3s" }}>
                    <Header toggleSidebar={toggleSidebar} />
                    <div className="breadcrumb-main-bx">
                        <div className="breadcrumb-bx">
                            <Link className="breadcrumb-link" to="/admin/dashboard">Dashboard</Link>
                            <div className="breadcrumb-link breadcrumb-disabled">/</div>
                            <Link className="breadcrumb-link" to="/admin/user">User</Link>
                            <div className="breadcrumb-link breadcrumb-disabled">/</div>
                            <div className="breadcrumb-link breadcrumb-active">Add Admin</div>
                        </div>
                    </div>
                    <section className="content">
                        <div className="container-fluid">

                            {alert.message && (
                                <div className={`alert alert-${alert.type}`} role="alert">
                                    {alert.message}
                                </div>
                            )}

                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card">
                                        <div className="card-header">Add Admin User</div>
                                        <div className="card-body">
                                            <form onSubmit={handleSubmit}>
                                                <div className="row">
                                                    <div className="form-group col-6">
                                                        <label htmlFor="name">Name <span className="text-danger">*</span></label>
                                                        <input
                                                            type="text"
                                                            className={`form-control ${validationErrors.name ? 'is-invalid' : ''}`}
                                                            id="name"
                                                            name="name"
                                                            placeholder="Enter admin name"
                                                            value={formData.name}
                                                            onChange={handleChange}
                                                        />
                                                        {validationErrors.name && <div className="invalid-feedback">{validationErrors.name}</div>}
                                                    </div>
                                                    <div className="form-group col-6">
                                                        <label htmlFor="email">Email <span className="text-danger">*</span></label>
                                                        <input
                                                            type="email"
                                                            className={`form-control ${validationErrors.email ? 'is-invalid' : ''}`}
                                                            id="email"
                                                            name="email"
                                                            placeholder="Enter email"
                                                            value={formData.email}
                                                            onChange={handleChange}
                                                        />
                                                        {validationErrors.email && <div className="invalid-feedback">{validationErrors.email}</div>}
                                                    </div>
                                                    <div className="form-group col-6">
                                                        <label htmlFor="password">Password <span className="text-danger">*</span></label>
                                                        <input
                                                            type="password"
                                                            className={`form-control ${validationErrors.password ? 'is-invalid' : ''}`}
                                                            id="password"
                                                            name="password"
                                                            placeholder="Enter password (min 6 characters)"
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                        />
                                                        {validationErrors.password && <div className="invalid-feedback">{validationErrors.password}</div>}
                                                    </div>
                                                    <div className="form-group col-6">
                                                        <label htmlFor="role">Role <span className="text-danger">*</span></label>
                                                        <select
                                                            className={`form-control ${validationErrors.role ? 'is-invalid' : ''}`}
                                                            id="role"
                                                            name="role"
                                                            value={formData.role}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="admin">Admin</option>
                                                            <option value="superadmin">Super Admin</option>
                                                        </select>
                                                        {validationErrors.role && <div className="invalid-feedback">{validationErrors.role}</div>}
                                                    </div>
                                                    <div className="form-group col-6">
                                                        <label>
                                                            <input
                                                                type="checkbox"
                                                                name="isActive"
                                                                checked={formData.isActive}
                                                                onChange={handleChange}
                                                                style={{ marginRight: "5px" }}
                                                            />
                                                            Active
                                                        </label>
                                                    </div>
                                                </div>
                                                <button type="submit" className="common-submit-btn mt-2" disabled={loading}>
                                                    {loading ? "Adding Admin..." : "Add Admin"}
                                                </button>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                    <Footer />
                </div>
            </div>
        </>
    );
}

export default AddUserDetails;
